<template>
  <main class="invitation-preferences-page">
    <section class="preferences-panel" aria-labelledby="preferences-title">
      <header class="preferences-header">
        <i class="material-icons" aria-hidden="true">tune</i>
        <p class="preferences-eyebrow">ChurchScheduling email preferences</p>
        <h1 id="preferences-title">Choose which invitations reach you.</h1>
      </header>

      <div v-if="loading" class="preferences-state" role="status">
        <span class="spinner-border text-success" aria-hidden="true"></span>
        <p>Loading your preferences...</p>
      </div>
      <div v-else-if="error" class="preferences-state" role="alert">
        <p>{{ error }}</p>
      </div>
      <form v-else class="preferences-form" @submit.prevent="savePreferences">
        <p class="preferences-intro">
          Manage future volunteer invitations sent to this email address. These choices do not affect account, schedule, or other service messages.
        </p>

        <label class="preference-option">
          <input v-model="preferences.inviter" type="checkbox" />
          <span><strong>From {{ details.inviter_name || 'this volunteer' }}</strong><small>Stop invitations from this person, at any Location.</small></span>
        </label>
        <label class="preference-option">
          <input v-model="preferences.location" type="checkbox" />
          <span><strong>From {{ details.location_name || 'this Location' }}</strong><small>Stop invitations from everyone at this Location.</small></span>
        </label>
        <label class="preference-option preference-option-platform">
          <input v-model="preferences.platform" type="checkbox" />
          <span><strong>All future volunteer invitations</strong><small>Stop volunteer invitations sent through ChurchScheduling.</small></span>
        </label>

        <p v-if="saved" class="preferences-saved" role="status">Your preferences have been updated.</p>
        <p v-if="saveError" class="preferences-error" role="alert">{{ saveError }}</p>
        <button class="preferences-submit" type="submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          {{ saving ? 'Saving...' : 'Save preferences' }}
        </button>
      </form>
      <footer class="preferences-footer">Your choices apply to volunteer invitations only.</footer>
    </section>
  </main>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/config/api';

const route = useRoute();
const token = String(route.params.token || '');
const loading = ref(true);
const saving = ref(false);
const saved = ref(false);
const error = ref('');
const saveError = ref('');
const details = ref({ inviter_name: '', location_name: '' });
const preferences = ref({ inviter: false, location: false, platform: false });

function applyResponse(data) {
  details.value = {
    inviter_name: data?.inviter_name || 'this volunteer',
    location_name: data?.location_name || 'this Location',
  };
  preferences.value = {
    inviter: data?.preferences?.inviter === true,
    location: data?.preferences?.location === true,
    platform: data?.preferences?.platform === true,
  };
}

async function loadPreferences() {
  try {
    const response = await api.get(`/volunteer-invitations/preferences/${encodeURIComponent(token)}`);
    applyResponse(response.data?.data);
  } catch (requestError) {
    error.value = requestError.response?.data?.message || 'This preferences link is invalid or unavailable.';
  } finally {
    loading.value = false;
  }
}

async function savePreferences() {
  if (saving.value) return;
  saving.value = true;
  saved.value = false;
  saveError.value = '';
  try {
    const scopes = Object.entries(preferences.value)
      .filter(([, selected]) => selected)
      .map(([scope]) => scope);
    const response = await api.post(`/volunteer-invitations/preferences/${encodeURIComponent(token)}`, { scopes });
    applyResponse(response.data?.data);
    saved.value = true;
  } catch (requestError) {
    saveError.value = requestError.response?.data?.message || 'Your preferences could not be saved. Please try again.';
  } finally {
    saving.value = false;
  }
}

onMounted(loadPreferences);
</script>

<style scoped>
.invitation-preferences-page { display: grid; min-height: 100vh; place-items: center; padding: 1rem; background: radial-gradient(ellipse at 50% 0%, #e7f3e9 0%, #f4f6f1 48%, #edf1ec 100%); color: #293d31; }
.preferences-panel { width: min(100%, 570px); overflow: hidden; border: 1px solid #d9e6da; border-radius: 8px; background: #fff; box-shadow: 0 18px 48px rgba(31, 67, 45, .12); }
.preferences-header { padding: 1.8rem 2rem; background: #176b4d; color: #fff; }
.preferences-header > .material-icons { display: grid; width: 44px; height: 44px; margin-bottom: 1rem; place-items: center; border: 1px solid rgba(255,255,255,.35); border-radius: 50%; background: rgba(255,255,255,.12); }
.preferences-eyebrow { margin: 0 0 .4rem; color: #cde9d8; font-size: .7rem; font-weight: 750; text-transform: uppercase; }
.preferences-header h1 { max-width: 430px; margin: 0; color: #fff; font-size: 1.55rem; line-height: 1.3; }
.preferences-form { padding: 1.6rem 2rem; }
.preferences-intro { margin: 0 0 1.2rem; color: #637268; font-size: .88rem; line-height: 1.6; }
.preference-option { display: flex; align-items: flex-start; gap: .8rem; padding: 1rem 0; border-top: 1px solid #e8eee8; cursor: pointer; }
.preference-option input { width: 18px; height: 18px; flex: 0 0 18px; margin-top: .15rem; accent-color: #176b4d; }
.preference-option span,.preference-option strong,.preference-option small { display: block; }
.preference-option strong { color: #354b3c; font-size: .9rem; }
.preference-option small { margin-top: .2rem; color: #748179; font-size: .78rem; line-height: 1.45; }
.preference-option-platform { border-bottom: 1px solid #e8eee8; }
.preferences-submit { display: inline-flex; min-height: 44px; align-items: center; justify-content: center; gap: .5rem; margin-top: 1.2rem; padding: .65rem 1.1rem; border: 0; border-radius: 5px; background: #176b4d; color: #fff; font-size: .86rem; font-weight: 700; }
.preferences-submit:disabled { opacity: .65; }
.preferences-saved,.preferences-error { margin: 1rem 0 0; font-size: .84rem; }
.preferences-saved { color: #28704b; }
.preferences-error { color: #a34135; }
.preferences-state { min-height: 210px; display: grid; place-content: center; justify-items: center; gap: .8rem; padding: 2rem; text-align: center; color: #637268; }
.preferences-state p { margin: 0; }
.preferences-footer { padding: .9rem 2rem; border-top: 1px solid #e8eee8; background: #fafcfa; color: #87938a; font-size: .72rem; text-align: center; }
@media (max-width: 575.98px) { .preferences-header { padding: 1.5rem 1.25rem; } .preferences-form { padding: 1.35rem 1.25rem; } }
</style>
